export const ROUTE_PREFIX = {
  PUBLIC: '/app',
  PRIVATE: '/cb',
  EMBEDDED: '/int',
};

/**
 * Maps /app route names to their direct /cb equivalents.
 * Only routes with a corresponding /cb counterpart are listed.
 * Used by the prefix-consistency guard to keep navigation within the /cb context.
 */
export const APP_TO_CB_ROUTE_MAP = {
  'schedule-pa': 'cb-schedule-pa',
  'schedule-only': 'cb-schedule-only',
  'schedule-start': 'cb-schedule-only',
  reschedule: 'cb-reschedule',
  'scheduled-start': 'cb-scheduled-start',
  'overbook-queue': 'cb-overbook-queue',
  'scheduled-test': 'cb-scheduled-test',
  'scheduled-test-overbook': 'cb-scheduled-test-overbook',
  meet: 'cb-meet',
};

export const getCbEquivalentRouteName = (appRouteName) => APP_TO_CB_ROUTE_MAP[appRouteName] || null;

/**
 * Determines whether an /app patient route should be rewritten to its /cb equivalent.
 * Used by the global router guard in BOTH mode to keep subscribers in backoffice context.
 */
export const shouldRewriteAppRouteToCb = ({ toPath, toName, fromPath, hasValidAuth }) => {
  if (!isAppContext(toPath)) return null;

  const cbEquivalent = getCbEquivalentRouteName(toName);
  if (!cbEquivalent) return null;

  if (isCbContext(fromPath) || hasValidAuth) return cbEquivalent;

  return null;
};

export const isCbContext = (path) => path.startsWith(ROUTE_PREFIX.PRIVATE);
export const isAppContext = (path) => path.startsWith(ROUTE_PREFIX.PUBLIC);
export const isEmbeddedContext = (path) => path.startsWith(ROUTE_PREFIX.EMBEDDED);

export const getConsultationHistoryRouteName = (pathOrRoute) => {
  const path = typeof pathOrRoute === 'string' ? pathOrRoute : pathOrRoute?.path || '';
  if (isEmbeddedContext(path)) return 'embedded-consultation-history';
  if (isAppContext(path)) return 'consultation-history';
  return null;
};

export const getScheduledRouteName = (pathOrRoute) => {
  const path = typeof pathOrRoute === 'string' ? pathOrRoute : pathOrRoute?.path || '';
  if (isEmbeddedContext(path)) return 'embedded-scheduled';
  if (isCbContext(path)) return 'cb-scheduled-start';
  if (isAppContext(path)) return 'scheduled-start';
  return null;
};

export const getScheduleStartRouteName = (pathOrRoute) => {
  const path = typeof pathOrRoute === 'string' ? pathOrRoute : pathOrRoute?.path || '';
  if (isEmbeddedContext(path)) return 'embedded-start';
  if (isAppContext(path)) return 'schedule-start';
  return null;
};

export const getCallTestRouteName = (pathOrRoute) => {
  const path = typeof pathOrRoute === 'string' ? pathOrRoute : pathOrRoute?.path || '';
  if (isEmbeddedContext(path)) return 'embedded-test';
  if (isCbContext(path)) return 'cb-scheduled-test';
  return 'scheduled-test';
};

export const getSchedulePaRouteName = (pathOrRoute) => {
  const path = typeof pathOrRoute === 'string' ? pathOrRoute : pathOrRoute?.path || '';
  if (isEmbeddedContext(path)) return 'embedded-schedule-pa';
  if (isCbContext(path)) return 'cb-schedule-pa';
  return 'schedule-pa';
};

export const isPatientConsultationFlow = (pathOrRoute) => {
  const path = typeof pathOrRoute === 'string' ? pathOrRoute : pathOrRoute?.path || '';
  return isAppContext(path) || isEmbeddedContext(path);
};

export const sanitizeRouteCpf = (cpf) => {
  if (!cpf) return null;
  const digits = String(cpf).replace(/\D/g, '');
  return digits || null;
};
